import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Trophy, Target, Award, ChevronRight } from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

export default function PromotionsRadarModule() {
  const [seuil, setSeuil] = useState(75);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/promotions/radar?seuil_pct=${seuil}`);
      setData(r.data);
    } catch {
      setData({ students: [], count: 0 });
    }
    setLoading(false);
  }, [seuil]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="promotions-radar-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<><Trophy size={36} className="text-impact-gold"/> Promotions à venir</>} subtitle={<>Élèves prêts pour leur prochain test de ceinture — planifie tes évaluations.</>} />
      </div>

      <div className="hard-border-b grid grid-cols-3 gap-0">
        <div className="hard-border-r px-6 py-5">
          <div className="ticker text-muted-foreground mb-1">PRÊTS À ÉVALUER</div>
          <div className="text-3xl font-black text-impact-gold" data-testid="count-ready">{data?.count ?? "—"}</div>
        </div>
        <div className="hard-border-r px-6 py-5">
          <div className="ticker text-muted-foreground mb-1">SEUIL ACTUEL</div>
          <div className="text-3xl font-black">≥ {seuil}%</div>
        </div>
        <div className="px-6 py-5">
          <div className="ticker text-muted-foreground mb-1">CONSEIL</div>
          <div className="text-sm">{(data?.count ?? 0) > 5 ? "📅 Bloque une soirée évaluation cette semaine." : "Continue les techniques manquantes en cours."}</div>
        </div>
      </div>

      <div className="px-8 lg:px-12 py-6">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Calcul en cours...</div>
        ) : !data || data.students.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Target size={48} className="mx-auto mb-3 opacity-50"/>
            <p>Aucun élève n'atteint le seuil de {seuil}% sur sa prochaine ceinture.</p>
            <p className="text-xs mt-2">Baisse le seuil ou continue de marquer les techniques maîtrisées dans le Carnet santé de chaque élève.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.students.map((s, i) => (
              <article key={s.contract_id} data-testid={`radar-row-${s.contract_id}`}
                className={`hard-border p-4 grid grid-cols-12 gap-4 items-center ${s.progression_pct >= 95 ? "bg-impact-gold/10 border-impact-gold" : ""}`}>
                <div className="col-span-1 text-center">
                  <div className="font-display font-black text-2xl">{i + 1}</div>
                </div>
                <div className="col-span-3">
                  <div className="font-display font-bold text-lg">{s.full_name}</div>
                  <div className="text-xs text-muted-foreground">{s.discipline?.replace(/_/g, " ")}</div>
                </div>
                <div className="col-span-3 flex items-center gap-2 text-sm">
                  <span className="w-4 h-4 rounded-full hard-border" style={{ background: s.ceinture_actuelle_couleur }}/>
                  <span>{s.ceinture_actuelle}</span>
                  <ChevronRight size={14} className="text-muted-foreground"/>
                  <span className="w-4 h-4 rounded-full hard-border" style={{ background: s.prochaine_ceinture_couleur }}/>
                  <span className="font-bold">{s.prochaine_ceinture}</span>
                </div>
                <div className="col-span-3">
                  <div className="hard-border h-3 bg-secondary/30 relative overflow-hidden">
                    <div className={`h-full ${s.progression_pct >= 95 ? "bg-impact-gold" : "bg-[#10b981]"}`} style={{ width: `${s.progression_pct}%` }}/>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-muted-foreground">{s.techniques_mastered}/{s.techniques_total}</span>
                    <span className="font-bold" data-testid={`pct-${s.contract_id}`}>{s.progression_pct}%</span>
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  {s.progression_pct >= 95 ? (
                    <span className="ticker bg-impact-gold text-foreground px-3 py-1.5 inline-flex items-center gap-1"><Award size={12}/> ÉVAL. PRIORITAIRE</span>
                  ) : (
                    <span className="ticker bg-secondary px-3 py-1.5">Reste {s.techniques_total - s.techniques_mastered} tech.</span>
                  )}
                </div>
                {s.techniques_manquantes?.length > 0 && (
                  <div className="col-span-12 mt-2 pt-2 border-t flex flex-wrap gap-1.5">
                    <span className="ticker text-xs text-muted-foreground">À VALIDER :</span>
                    {s.techniques_manquantes.slice(0, 8).map((t, idx) => (
                      <span key={idx} className="text-xs bg-secondary/40 px-2 py-0.5 hard-border">{t}</span>
                    ))}
                    {s.techniques_manquantes.length > 8 && <span className="text-xs text-muted-foreground">+{s.techniques_manquantes.length - 8}</span>}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
